// src/components/account/AccountProfile.jsx
import * as React from 'react';
import {
  Paper, Typography, Stack, Button, Alert, TextField, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getUser, clearAuth } from '../../lib/auth';
import { api } from '../../lib/api';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import accountProfileI18n from '../../i18n/components/account/accountprofile.i18n';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';
const BLUE_BG = 'rgba(11,95,255,0.06)';
const ERROR_BG = '#ffebee';
const ERROR_BORDER = '#ef9a9a';

// Champs requis pour considérer le profil d’expédition comme “complet”
// 👉 le téléphone n’en fait plus partie
const REQUIRED_PROFILE_FIELDS = ['firstname', 'name', 'address', 'postalCode', 'city'];

function maskIban(iban = '') {
  const s = String(iban).replace(/\s+/g, '');
  if (!s) return '—';
  if (s.length <= 8) return '••••';
  return `${s.slice(0, 4)} •• •• •• •• •• •• •• ${s.slice(-2)}`.replace(/\s{2,}/g, ' ');
}

// Validation IBAN (format + mod 97)
function isValidIban(ibanRaw = '') {
  const iban = ibanRaw.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let converted = '';
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    if (code >= 65 && code <= 90) converted += (code - 55).toString();
    else converted += ch;
  }
  let remainder = 0;
  for (let i = 0; i < converted.length; i += 7) {
    const part = String(remainder) + converted.slice(i, i + 7);
    remainder = Number(part) % 97;
  }
  return remainder === 1;
}

// Helper : déterminer si un profil (objet issu du back) est complet
function computeProfileComplete(src = {}) {
  const values = {
    firstname:  src.firstname,
    name:       src.name,
    address:    src.address,
    postalCode: src.postalCode,
    city:       src.city,
  };
  return REQUIRED_PROFILE_FIELDS.every((field) => {
    const v = values[field];
    return v !== null && v !== undefined && String(v).trim() !== '';
  });
}

export default function AccountProfile() {
  const user = getUser();

  const { t, register } = useI18n();
  React.useEffect(() => { register('accountProfile', accountProfileI18n); }, [register]);

  // IBAN
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [ibanMasked, setIbanMasked] = React.useState('—');
  const [hasIban, setHasIban] = React.useState(false);

  const [editingIban, setEditingIban] = React.useState(false);
  const [iban, setIban] = React.useState('');
  const [ownerName, setOwnerName] = React.useState(user?.firstname || user?.name || '');

  // Profil expédition
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [profileErr, setProfileErr] = React.useState('');
  const [profileOk, setProfileOk] = React.useState('');

  const [firstname, setFirstname] = React.useState(user?.firstname || '');
  const [name, setName]           = React.useState(user?.name || '');
  const [phone, setPhone]         = React.useState(user?.phone || '');
  const [address, setAddress]     = React.useState(user?.address || '');
  const [postalCode, setPostalCode] = React.useState(user?.postalCode || '');
  const [city, setCity]           = React.useState(user?.city || '');

  // État du profil tel qu’il est connu par la DB
  const [profileCompleteServer, setProfileCompleteServer] = React.useState(false);
  // Mode édition du profil expédition
  const [profileEditing, setProfileEditing] = React.useState(false);

  const logout = () => { clearAuth(); window.location.assign('/services'); };

  const loadIban = async () => {
    setErr(''); setOk('');
    try {
      const { data } = await api.get('/iban/me');
      setIbanMasked(data?.ibanMasked || '—');
      setHasIban(!!data?.hasIban);
      if (data?.ownerName) setOwnerName(data.ownerName);
    } catch (e) {
      // pas bloquant
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    setProfileErr(''); setProfileOk('');
    try {
      const { data } = await api.get('/users/me');
      const src = data || {};
      setFirstname(src.firstname || '');
      setName(src.name || '');
      setPhone(src.phone || ''); // 📞 toujours chargé pour l’affichage en haut
      setAddress(src.address || '');
      setPostalCode(src.postalCode || '');
      setCity(src.city || '');

      // ✅ On se base sur ce que renvoie la DB pour l’état "complet"
      setProfileCompleteServer(computeProfileComplete(src));
      setProfileEditing(false);
    } catch (e) {
      setProfileErr(t('accountProfile.errors.loadProfile'));
    } finally {
      setProfileLoading(false);
    }
  };

  React.useEffect(() => {
    loadIban();
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveIban = async () => {
    setErr(''); setOk('');
    const trimmed = iban.replace(/\s+/g, '');
    if (!isValidIban(trimmed)) {
      setErr(t('accountProfile.errors.invalidIban'));
      return;
    }
    try {
      await api.post('/iban/upsert', { iban: trimmed, ownerName: ownerName || undefined });
      setOk(t('accountProfile.messages.saved'));
      setEditingIban(false);
      setIban('');
      await loadIban();
    } catch (e) {
      setErr(e?.response?.data?.message || t('accountProfile.errors.saveFailed'));
    }
  };

  const saveProfile = async () => {
    setProfileErr(''); setProfileOk('');
    try {
      // ❗️On ne touche plus au téléphone depuis cette section
      const payload = {
        firstname:  firstname || null,
        name:       name || null,
        address:    address || null,
        postalCode: postalCode || null,
        city:       city || null,
      };

      const { data } = await api.patch(`/users/${user.id}`, payload);
      const updated = data || {};

      setProfileOk(t('accountProfile.profileMessages.saved'));
      // ✅ Recalcule état complet depuis la réponse back
      const isComplete = computeProfileComplete(updated);
      setProfileCompleteServer(isComplete);
      setProfileEditing(false);
    } catch (e) {
      setProfileErr(e?.response?.data?.message || t('accountProfile.errors.profileSaveFailed'));
    }
  };

  const copyMasked = () => {
    if (!ibanMasked || ibanMasked === '—') return;
    navigator.clipboard?.writeText(ibanMasked);
    setOk(t('accountProfile.messages.copied'));
    setTimeout(() => setOk(''), 1500);
  };

  const lockedProfile = profileCompleteServer && !profileEditing;
  const canShowSaveProfile = !profileCompleteServer || profileEditing;

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        {t('accountProfile.title')}
      </Typography>

      {/* Infos simples */}
      <Stack spacing={1.2} sx={{ mb: 2 }}>
        <Typography>
          {t('accountProfile.email')} : {user?.email}
        </Typography>
        <Typography>
          {t('accountProfile.company')} : {user?.companyName || '—'}
        </Typography>
        <Typography>
          {t('accountProfile.profile.phone')} : {phone || '—'}
        </Typography>
      </Stack>

      {/* 🧾 Profil expédition (rouge si incomplet, bloqué si complet) */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: profileCompleteServer ? '1px solid #e6efe8' : `1px solid ${ERROR_BORDER}`,
          background: profileCompleteServer ? '#fff' : ERROR_BG,
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('accountProfile.profile.title')}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {profileCompleteServer ? (
              <>
                <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'success.main' }}
                >
                  {t('accountProfile.profile.statusComplete')}
                </Typography>
                {!profileEditing && (
                  <Tooltip title={t('accountProfile.profile.edit')}>
                    <IconButton
                      size="small"
                      onClick={() => setProfileEditing(true)}
                      sx={{ ml: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'error.main' }}
              >
                {t('accountProfile.profile.statusIncomplete')}
              </Typography>
            )}
          </Stack>
        </Stack>

        {!profileCompleteServer && (
          <Typography
            variant="caption"
            sx={{ color: 'error.main', mb: 1.5, display: 'block' }}
          >
            {t('accountProfile.profile.hint')}
          </Typography>
        )}

        {profileErr && <Alert severity="error" sx={{ mb: 1 }}>{profileErr}</Alert>}
        {profileOk &&  <Alert severity="success" sx={{ mb: 1 }}>{profileOk}</Alert>}

        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label={t('accountProfile.profile.firstname')}
              size="small"
              fullWidth
              value={firstname}
              disabled={lockedProfile || profileLoading}
              onChange={(e) => setFirstname(e.target.value)}
            />
            <TextField
              label={t('accountProfile.profile.name')}
              size="small"
              fullWidth
              value={name}
              disabled={lockedProfile || profileLoading}
              onChange={(e) => setName(e.target.value)}
            />
          </Stack>

          {/* 👉 Téléphone retiré de la section expédition */}

          <TextField
            label={t('accountProfile.profile.address')}
            size="small"
            fullWidth
            value={address}
            disabled={lockedProfile || profileLoading}
            onChange={(e) => setAddress(e.target.value)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label={t('accountProfile.profile.postalCode')}
              size="small"
              fullWidth
              value={postalCode}
              disabled={lockedProfile || profileLoading}
              onChange={(e) => setPostalCode(e.target.value)}
            />
            <TextField
              label={t('accountProfile.profile.city')}
              size="small"
              fullWidth
              value={city}
              disabled={lockedProfile || profileLoading}
              onChange={(e) => setCity(e.target.value)}
            />
          </Stack>

          {canShowSaveProfile && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="contained"
                onClick={saveProfile}
                disabled={profileLoading}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
              >
                {t('accountProfile.profile.save')}
              </Button>
              {profileCompleteServer && profileEditing && (
                <Button onClick={() => loadProfile()}>
                  {t('accountProfile.form.cancel')}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* 💳 Bloc IBAN existant */}
      <Stack
        spacing={1.5}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid #e6efe8',
          background: BLUE_BG,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountBalanceIcon sx={{ color: BLUE }} />
            <Typography sx={{ fontWeight: 700 }}>
              {t('accountProfile.bank.title')}
            </Typography>
          </Stack>
          {!editingIban && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditingIban(true)}
              sx={{ color: BLUE }}
            >
              {hasIban ? t('accountProfile.bank.edit') : t('accountProfile.bank.add')}
            </Button>
          )}
        </Stack>

        {!editingIban ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography>
                {t('accountProfile.bank.iban')} : <b>{loading ? '...' : ibanMasked}</b>
              </Typography>
              {hasIban && (
                <IconButton size="small" onClick={copyMasked} sx={{ color: BLUE }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('accountProfile.bank.rgpd')}
            </Typography>
          </>
        ) : (
          <>
            <TextField
              label={t('accountProfile.form.owner')}
              size="small"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('accountProfile.form.iban')}
              size="small"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="PL76 3000 6000 0112 3456 7890 189"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {iban && isValidIban(iban) && <CheckIcon sx={{ color: BLUE }} fontSize="small" />}
                  </InputAdornment>
                )
              }}
              helperText={t('accountProfile.form.helper')}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={saveIban}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
              >
                {t('accountProfile.form.save')}
              </Button>
              <Button
                onClick={() => { setEditingIban(false); setIban(''); setErr(''); }}
              >
                {t('accountProfile.form.cancel')}
              </Button>
            </Stack>
          </>
        )}

        {ok && <Alert severity="success">{ok}</Alert>}
        {err && <Alert severity="error">{err}</Alert>}
      </Stack>

      <Stack direction="row" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={logout}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
        >
          {t('accountProfile.logout')}
        </Button>
      </Stack>
    </Paper>
  );
}